/* ============================================
   ROOM DESIGNER — Door Module
   Asset file: custom-room-designer-door.js

   Handles door tool (click, swing computation),
   door preview drawing, door swing editing,
   door gap clearing, and door graphic rendering.
   Loaded before custom-room-designer.js via <script defer>.
   Instantiated by the RoomDesigner constructor.
   ============================================ */

class RoomDesignerDoor {
  constructor(rd) {
    this.rd = rd;
    this._installMethods();
  }

  _installMethods() {
    const rd = this.rd;
    // Bind each method onto the RD instance so existing call sites
    // (this.drawDoor(...), this.handleDoorClick(...), etc.) keep working.
    // .bind(rd) ensures 'this' inside each method refers to the RD instance.
    rd.handleDoorClick = this.handleDoorClick.bind(rd);
    rd._computeSwingFromClick = this._computeSwingFromClick.bind(rd);
    rd._computeSwingForDoor = this._computeSwingForDoor.bind(rd);
    rd._getSwingOptions = this._getSwingOptions.bind(rd);
    rd._drawDoorSwingArc = this._drawDoorSwingArc.bind(rd);
    rd._drawDoorPreview = this._drawDoorPreview.bind(rd);
    rd._drawDoorToNearestDoorDimension = this._drawDoorToNearestDoorDimension.bind(rd);
    rd._drawDoorSwingEdit = this._drawDoorSwingEdit.bind(rd);
    rd._drawDoorDragMeasurements = this._drawDoorDragMeasurements.bind(rd);
    rd._getWallOutwardSign = this._getWallOutwardSign.bind(rd);
    rd._getDoorOffset = this._getDoorOffset.bind(rd);
    rd._clearDoorGap = this._clearDoorGap.bind(rd);
    rd.drawDoor = this.drawDoor.bind(rd);
    rd.drawDoorGraphic = this.drawDoorGraphic.bind(rd);
    // CUSTOM_ROOMDESIGNER_BY_CLAUDE — doors on curved walls
    rd._placeDoorOnCurve = this._placeDoorOnCurve.bind(rd);
    rd._commitCurveDoor = this._commitCurveDoor.bind(rd);
    rd._drawCurveDoorPreview = this._drawCurveDoorPreview.bind(rd);
    rd._doorSpanOnWall = this._doorSpanOnWall.bind(rd);
    rd._warnIfCurveOpeningCoarse = this._warnIfCurveOpeningCoarse.bind(rd);
    rd._reconcileCurveOpenings = this._reconcileCurveOpenings.bind(rd);
    rd._curveDoorToNeighbour = this._curveDoorToNeighbour.bind(rd);
    rd._straightDoorToCurve = this._straightDoorToCurve.bind(rd);
    // END CUSTOM_ROOMDESIGNER_BY_CLAUDE
  }

  /* ── DOORS ON CURVED WALLS ──────────────────────────────────────
     CUSTOM_ROOMDESIGNER_BY_CLAUDE

     A curve is N straight facets; a 20ft curve's facets are ~10in, so no door fits
     on one. Instead the door reserves an OPENING on the curve, which tessellation
     turns into exactly one facet spanning the door's width. The door then binds to
     an ordinary straight wall and the rest of the door subsystem is untouched.

     Placement skips the usual phase-2 "pick which way the door runs": on a curve the
     opening is simply centred on the click, since both directions follow the same
     arc. Swing doors still go to phase 3 to choose the swing. */

  /** Reserve an opening on the clicked curve and start/complete placement. */
  _placeDoorOnCurve(nearest, worldPoint, doorWidthFeet) {
    const CV = window.RoomDesignerCurve;
    const owner = nearest.room || this._findRoomForWall(nearest.wall);
    // Never fail silently here — a no-op click on a wall is indistinguishable from
    // a broken tool, and that cost a whole debugging round once already.
    if (!CV || !owner) { this.updateStatus('Could not resolve this curved wall.', { error: true }); return; }
    const curveId = nearest.wall.curveId;
    const facets = this._curveFacetsOf(owner, curveId);
    if (facets.length === 0) { this.updateStatus('Could not resolve this curved wall.', { error: true }); return; }
    const arc = facets[0].curveArc;
    const ends = CV.endpointsOf(facets);

    // Does the opening physically fit this radius at all?
    const dt = CV.spanForChord(arc, doorWidthFeet);
    if (!isFinite(dt)) {
      this.updateStatus(
        'This opening is too wide for a ' + this.formatMeasurement(arc.r) +
        ' radius — straighten the wall or use a narrower opening.', { error: true });
      return;
    }
    const tc = CV.paramAtPoint(arc, ends.start, ends.end, worldPoint);
    const id = 'op_' + Math.round(tc * 1e6) + '_' + (arc.openings || []).length;
    const placed = CV.fitOpening(arc, arc.openings, tc, doorWidthFeet, id);
    if (!placed) { this.updateStatus('No room for a door here.', { error: true }); return; }

    // A roller commits on this click. A swing door needs a second click, so DON'T
    // touch the curve yet — cutting the opening now would leave a hole in the wall
    // if the user then hit Esc or switched tools, and there are a dozen paths that
    // clear doorPlacement. Nothing is mutated until the door actually commits.
    if (this.doorType === 'roller') {
      this.saveUndoState();
      if (!this._commitCurveDoor(owner, curveId, placed, null)) {
        this.updateStatus('No room for a door here.', { error: true });
        return;
      }
      this.doorPlacement = null;
      this._warnIfCurveOpeningCoarse(arc, doorWidthFeet);
      this.updateStatus('Roller door placed on the curved wall.');
      if (navigator.vibrate) navigator.vibrate(50);
      this.render(); this.updateMeasurements();
      return;
    }

    // Stand-in wall spanning where the opening WILL be, so swing selection and the
    // preview have real jamb points to work from without the curve being cut yet.
    const dtP = CV.spanForChord(arc, doorWidthFeet);
    const jambA = CV.pointAtParam(arc, ends.start, ends.end, placed.tc - dtP / 2);
    const jambB = CV.pointAtParam(arc, ends.start, ends.end, placed.tc + dtP / 2);
    this.doorPlacement = {
      wall: { start: jambA, end: jambB },
      room: owner,
      hingeParam: 0, endParam: 1,
      hingePoint: jambA,
      onCurve: true, curveId: curveId, pendingOpening: placed, phase: 3
    };
    this._warnIfCurveOpeningCoarse(arc, doorWidthFeet);
    this.updateStatus('Click to choose swing direction, or click on the door line for a closed door. Esc to go back.');
    this.render();
  }

  /**
   * Where a door of `widthFt` lands on a straight wall for a cursor at `worldPoint`,
   * under single-click centred placement.
   *
   * The door is centred on the cursor, then the JAMB nearer the closer wall end is
   * snapped to the grid — not the centre. You set out from a corner to a jamb, so
   * that is the dimension that should land on a round number; snapping the centre
   * would leave both jambs on half-steps for odd door widths.
   *
   * The preview and the click both go through here, so what you see is what you get.
   */
  _doorSpanOnWall(wall, worldPoint, widthFt, room) {
    const wallLen = this.getWallLength(wall);
    if (!(wallLen > 0)) return { loP: 0, hiP: 0, canPlace: false };
    const centre = this.getWallParam(worldPoint, wall) * wallLen;
    const step = this._currentGridStep() || (1 / 12);
    let lo = centre - widthFt / 2;
    let hi = centre + widthFt / 2;
    if (centre <= wallLen / 2) { lo = Math.round(lo / step) * step; hi = lo + widthFt; }
    else { hi = Math.round(hi / step) * step; lo = hi - widthFt; }
    if (lo < 0) { lo = 0; hi = widthFt; }
    if (hi > wallLen) { hi = wallLen; lo = wallLen - widthFt; }
    const loP = lo / wallLen, hiP = hi / wallLen;
    const fits = widthFt <= wallLen + 0.01 && loP >= -0.001 && hiP <= 1.001;
    const canPlace = fits &&
      !this._doorsOverlap(wall, room, loP, hiP, null) &&
      !this._doorBlockedByConstraints(wall, loP, hiP, widthFt);
    return { loP: loP, hiP: hiP, canPlace: canPlace };
  }

  /**
   * Hover preview for a door over a curved wall: a ghost of the opening that WOULD
   * be cut, spanning the arc, plus the usual green/red dot. Mirrors what
   * _placeDoorOnCurve will do on click, so what you see is what you get.
   */
  _drawCurveDoorPreview(nearest, scale, doorWidthFeet) {
    const CV = window.RoomDesignerCurve;
    const owner = nearest.room || this._findRoomForWall(nearest.wall);
    if (!CV || !owner || !this.mouseWorldRaw) return;
    const facets = this._curveFacetsOf(owner, nearest.wall.curveId);
    if (facets.length === 0) return;
    const arc = facets[0].curveArc;
    const ends = CV.endpointsOf(facets);

    const dt = CV.spanForChord(arc, doorWidthFeet);
    const tcRaw = CV.paramAtPoint(arc, ends.start, ends.end, this.mouseWorldRaw);
    const placed = isFinite(dt) ? CV.fitOpening(arc, arc.openings, tcRaw, doorWidthFeet, '__preview') : null;
    const canPlace = !!placed;
    const tc = placed ? placed.tc : tcRaw;
    const half = isFinite(dt) ? dt / 2 : 0;

    const a = CV.pointAtParam(arc, ends.start, ends.end, Math.max(0, tc - half));
    const b = CV.pointAtParam(arc, ends.start, ends.end, Math.min(1, tc + half));
    const dx = b.x - a.x, dy = b.y - a.y;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    const dirX = dx / len, dirY = dy / len;
    const perpX = -dirY, perpY = dirX;
    const off = this._getDoorOffset(nearest.wall, perpX, perpY, owner);

    this.ctx.save();
    if (canPlace) {
      const jambA = { x: a.x + off.x, y: a.y + off.y };
      const jambB = { x: b.x + off.x, y: b.y + off.y };
      // CUSTOM_ROOMDESIGNER_BY_CLAUDE — erase the wall band under the door FIRST, as
      // the straight-wall preview does. Without it the leaf was painted at 0.45 alpha
      // straight over the solid wall and simply didn't read: the dimensions showed,
      // the door didn't. The gap is what makes a door preview look like a door.
      //
      // A straight erase across a curved wall is correct, not an approximation:
      // committing re-tessellates so the opening IS one flat facet spanning the door
      // (curve -> jamb -> flat door -> jamb -> curve), so this is exactly the shape
      // you get on click.
      this._clearDoorGap(jambA, jambB, this.wallThickness + 3 / scale, scale);
      this.drawDoorGraphic(jambA, jambB, dirX, dirY, perpX, perpY, this.doorType, scale, '#006BCC', 0.45);
    }
    const c = CV.pointAtParam(arc, ends.start, ends.end, tc);
    const dotR = Math.max(this.wallThickness * 0.5, 5 / scale);
    this.ctx.globalAlpha = 1.0;
    this.ctx.fillStyle = canPlace ? 'rgba(0, 200, 0, 0.6)' : 'rgba(204, 0, 0, 0.6)';
    this.ctx.beginPath();
    this.ctx.arc(c.x + off.x, c.y + off.y, dotR, 0, Math.PI * 2);
    this.ctx.fill();

    // Position measurements: from each end of the wall to the nearest edge of the
    // door, measured ALONG the arc (the distance you'd actually set out on site).
    // A neighbouring opening takes over as the reference if one sits between the
    // door and the wall end, matching how the straight-wall preview measures to the
    // nearest break point rather than always to the corner.
    if (canPlace) {
      const arcLen = CV.arcLength(arc);
      const neighbours = CV.resolveOpenings(arc, (arc.openings || [])) || [];
      let loBound = 0, hiBound = 1;
      for (const o of neighbours) {
        if (o.t1 <= tc - half + 1e-9) loBound = Math.max(loBound, o.t1);
        else if (o.t0 >= tc + half - 1e-9) hiBound = Math.min(hiBound, o.t0);
      }

      // Draw on the room-interior side, same as the straight-wall dimensions.
      const uc = { x: (c.x - arc.cx) / arc.r, y: (c.y - arc.cy) / arc.r };
      const outSign = this._getWallOutwardSign(nearest.wall, perpX, perpY, owner) || 1;
      const radialOutward = (uc.x * perpX + uc.y * perpY) * outSign > 0;
      const side = radialOutward ? -1 : 1;

      const fontSize = (11 + Math.min(7, Math.max(0, (scale - 20) / 12))) / scale;
      const labelOffset = Math.max(this.wallThickness * 2.5, 22 / scale);
      const dimPerp = Math.max(this.wallThickness * 2.0, 16 / scale);
      const nubLen = Math.max(this.wallThickness * 0.4, 4 / scale);
      const radial = (t, extra) => {
        const p = CV.pointAtParam(arc, ends.start, ends.end, t);
        const ux = (p.x - arc.cx) / arc.r, uy = (p.y - arc.cy) / arc.r;
        return { x: p.x + ux * extra, y: p.y + uy * extra, ux: ux, uy: uy };
      };

      const drawArcDim = (t0, t1) => {
        const dist = arcLen * (t1 - t0);
        if (dist < 0.01) return;
        const gap = side * dimPerp;
        this.ctx.strokeStyle = 'rgba(0, 107, 204, 0.55)';
        this.ctx.lineWidth = 1.5 / scale;
        this.ctx.setLineDash([2 / scale, 2 / scale]);
        this.ctx.beginPath();
        const STEPS = 14;
        for (let k = 0; k <= STEPS; k++) {
          const p = radial(t0 + (t1 - t0) * (k / STEPS), gap);
          if (k === 0) this.ctx.moveTo(p.x, p.y); else this.ctx.lineTo(p.x, p.y);
        }
        this.ctx.stroke();
        this.ctx.setLineDash([]);
        // Radial nubs at both ends of the run.
        this.ctx.beginPath();
        for (const t of [t0, t1]) {
          const p = radial(t, gap);
          this.ctx.moveTo(p.x - p.ux * nubLen, p.y - p.uy * nubLen);
          this.ctx.lineTo(p.x + p.ux * nubLen, p.y + p.uy * nubLen);
        }
        this.ctx.stroke();
        const mid = radial((t0 + t1) / 2, side * labelOffset);
        this.ctx.font = `${fontSize}px Arial`;
        this.ctx.fillStyle = '#006BCC';
        this.ctx.textAlign = 'center';
        this.ctx.textBaseline = 'middle';
        this.ctx.fillText(this.formatMeasurement(dist), mid.x, mid.y);
      };

      drawArcDim(loBound, tc - half);
      drawArcDim(tc + half, hiBound);
    }
    this.ctx.restore();
  }

  /**
   * Cut the opening and create the door. The single point where a curve is actually
   * mutated for a door, so every cancel path before this is automatically safe.
   */
  _commitCurveDoor(owner, curveId, opening, swing) {
    const CV = window.RoomDesignerCurve;
    if (!CV || !owner) return false;
    const facets = this._curveFacetsOf(owner, curveId);
    if (facets.length === 0) return false;
    const arc = facets[0].curveArc;
    const next = ((arc && arc.openings) || []).concat([opening]);
    const newFacets = this._curveSetOpenings(owner, curveId, next);
    if (!newFacets) return false;
    const idx = CV.openingFacetIndex(newFacets[0].curveArc, opening.id);
    if (idx < 0) return false;
    owner.doors.push({
      wall: newFacets[idx], hingeParam: 0, endParam: 1,
      type: this.doorType, width: this.doorWidth,
      swing: swing || null, openingId: opening.id
    });
    return true;
  }

  /**
   * A wide opening on a tight radius bows noticeably away from its chord. The wall
   * still reads correctly (the chord is inside the gap, which gets erased) but the
   * door symbol is straight, so say so rather than let it look like a glitch.
   */
  _warnIfCurveOpeningCoarse(arc, doorWidthFeet) {
    if (!arc || !arc.r) return;
    const deviation = (doorWidthFeet * doorWidthFeet) / (8 * arc.r);   // sagitta of the opening
    if (deviation > 2 / 12) {
      this.updateStatus(
        'Placed — but this opening spans ' + this.formatMeasurement(deviation) +
        ' of curve, so it will read flatter than the wall around it.');
    }
  }

  /**
   * Drop any curve opening whose door has gone, and rebuild the affected curves so
   * the wall closes back up. Idempotent and cheap when nothing changed, so it is
   * safe to call after ANY door deletion — of which there are several paths
   * (eraser, delete-selection, mobile). Missing a call site leaves a cosmetic gap
   * rather than a broken binding, and the next reconcile repairs it.
   */
  _reconcileCurveOpenings(owner) {
    const CV = window.RoomDesignerCurve;
    if (!CV || !owner) return false;
    const list = owner.walls || owner.segments;
    if (!list) return false;
    const curveIds = new Set();
    for (const w of list) { if (w.curveId) curveIds.add(w.curveId); }
    if (curveIds.size === 0) return false;
    const live = new Set();
    for (const d of (owner.doors || [])) { if (d.openingId) live.add(d.openingId); }
    let changed = false;
    for (const cid of curveIds) {
      const facets = this._curveFacetsOf(owner, cid);
      if (!facets.length) continue;
      const arc = facets[0].curveArc;
      const ops = (arc && arc.openings) || [];
      if (!ops.length) continue;
      const keep = ops.filter(o => live.has(o.id));
      if (keep.length !== ops.length && this._curveSetOpenings(owner, cid, keep)) changed = true;
    }
    return changed;
  }

  /* ── SLIDING ACROSS THE CURVE / STRAIGHT BOUNDARY ──
     A door already snakes from wall to wall at a corner. A curve is a run of facets,
     so the handoff needs two bridges: leaving a curve has to start from the curve's
     TERMINAL facet (the opening facet's own ends are interior to the curve), and
     arriving at a curve has to convert the door into a reserved opening. */

  /**
   * Hand a door off the end of its curve onto the adjoining wall.
   * `whichEnd` is the curve terminus it ran into: 'start' or 'end'.
   * Returns true if it moved across.
   */
  _curveDoorToNeighbour(door, owner, curveId, whichEnd) {
    const CV = window.RoomDesignerCurve;
    if (!CV || !owner) return false;
    const facets = this._curveFacetsOf(owner, curveId);
    if (facets.length === 0) return false;
    const terminal = whichEnd === 'start' ? facets[0] : facets[facets.length - 1];
    const nextWall = this._findNextWallAtCorner(terminal, owner, whichEnd);
    if (!nextWall) return false;
    if (nextWall.curveId) return false;            // curve-to-curve: not supported
    const widthFt = door.width / 12;
    const nextLen = this.getWallLength(nextWall);
    if (nextLen < widthFt - 0.01) return false;    // won't fit on the next wall

    // Which end of the next wall touches this corner?
    const pkey = (p) => `${(Math.round(p.x * 1200) / 1200).toFixed(4)},${(Math.round(p.y * 1200) / 1200).toFixed(4)}`;
    const corner = whichEnd === 'start' ? terminal.start : terminal.end;
    const enteredFromStart = pkey(nextWall.start) === pkey(corner);
    const halfP = (widthFt / 2) / nextLen;
    const centre = enteredFromStart ? halfP : 1 - halfP;
    const pw = widthFt / nextLen;
    const reversed = door.hingeParam > door.endParam;

    // Re-point the door FIRST: _curveSetOpenings deletes doors whose opening has
    // gone, and this one is deliberately leaving rather than being deleted.
    const openingId = door.openingId;
    door.wall = nextWall;
    door.openingId = undefined;
    door.hingeParam = reversed ? centre + pw / 2 : centre - pw / 2;
    door.endParam = reversed ? centre - pw / 2 : centre + pw / 2;

    const arc = facets[0].curveArc;
    const remaining = ((arc && arc.openings) || []).filter(o => o.id !== openingId);
    this._curveSetOpenings(owner, curveId, remaining);
    return true;
  }

  /**
   * Take a door arriving at a curve and convert it into a reserved opening near the
   * terminus it entered by. Returns true if it moved across.
   */
  _straightDoorToCurve(door, owner, facetWall, corner) {
    const CV = window.RoomDesignerCurve;
    if (!CV || !owner || !facetWall || !facetWall.curveId) return false;
    const curveId = facetWall.curveId;
    const facets = this._curveFacetsOf(owner, curveId);
    if (facets.length === 0) return false;
    const arc = facets[0].curveArc;
    const ends = CV.endpointsOf(facets);
    const widthFt = door.width / 12;
    const dt = CV.spanForChord(arc, widthFt);
    if (!isFinite(dt)) return false;               // too wide for this radius

    // Enter at whichever terminus the shared corner is.
    const dStart = Math.hypot(corner.x - ends.start.x, corner.y - ends.start.y);
    const dEnd = Math.hypot(corner.x - ends.end.x, corner.y - ends.end.y);
    const target = dStart <= dEnd ? dt / 2 + CV.MIN_JAMB : 1 - dt / 2 - CV.MIN_JAMB;
    const id = 'op_x' + Math.round(target * 1e6) + '_' + ((arc.openings || []).length);
    const placed = CV.fitOpening(arc, arc.openings, target, widthFt, id);
    if (!placed) return false;

    const next = ((arc.openings) || []).concat([placed]);
    const newFacets = this._curveSetOpenings(owner, curveId, next);
    if (!newFacets) return false;
    const liveArc = newFacets[0].curveArc;
    const idx = CV.openingFacetIndex(liveArc, id);
    if (idx < 0) return false;
    const reversed = door.hingeParam > door.endParam;
    door.wall = newFacets[idx];
    door.openingId = id;
    door.hingeParam = reversed ? 1 : 0;
    door.endParam = reversed ? 0 : 1;
    return true;
  }

  /* END CUSTOM_ROOMDESIGNER_BY_CLAUDE */

  /* ── DOOR TOOL ── */

  handleDoorClick(worldPoint) {
    const doorWidthFeet = this.doorWidth / 12;
    if (!this.doorPlacement) {
      // Phase 1: set hinge point
      const nearest = this.findNearestWall(worldPoint, 2);
      if (!nearest) return;
      // CUSTOM_ROOMDESIGNER_BY_CLAUDE — a click on a curve reserves an OPENING on the
      // whole curve rather than trying to fit the door on one ~10in facet. Handled
      // before the wall-length check, which a facet could never pass.
      if (nearest.wall.curveId) {
        this._placeDoorOnCurve(nearest, worldPoint, doorWidthFeet);
        return;
      }
      // END CUSTOM_ROOMDESIGNER_BY_CLAUDE
      const wallLen = this.getWallLength(nearest.wall);
      if (wallLen < doorWidthFeet - 0.01) { this.updateStatus('Wall too short for this door.', { error: true }); return; }
      // CUSTOM_ROOMDESIGNER_BY_CLAUDE — single-click centred placement (Ryan, after
      // using the curved-wall flow). The door lands centred on the click with its
      // near jamb on the grid, so the old phase 2 — pick which DIRECTION the door
      // runs from the hinge — no longer has a question to answer and is skipped.
      // A roller is finished at this point; a swing door still picks its swing, and
      // that click is also what decides which end is the hinge.
      // Phase 2 is left in place below because the mobile flow still uses it.
      const span = this._doorSpanOnWall(nearest.wall, worldPoint, doorWidthFeet, nearest.room);
      if (!span.canPlace) { this.updateStatus('No room for a door here.', { error: true }); return; }
      if (this.doorType === 'roller') {
        this.saveUndoState();
        nearest.room.doors.push({
          wall: nearest.wall, hingeParam: span.loP, endParam: span.hiP,
          type: 'roller', width: this.doorWidth, swing: null
        });
        this.doorPlacement = null;
        this.updateStatus('Roller door placed!');
        if (navigator.vibrate) navigator.vibrate(50);
        this.render(); this.updateMeasurements();
        return;
      }
      this.doorPlacement = {
        wall: nearest.wall, room: nearest.room,
        hingeParam: span.loP, endParam: span.hiP,
        hingePoint: this.paramToPoint(nearest.wall, span.loP),
        phase: 3
      };
      this.updateStatus('Click to choose swing direction, or click on the door line for a closed door. Esc to go back.');
      this.render();
      // END CUSTOM_ROOMDESIGNER_BY_CLAUDE
    } else if (this.doorPlacement.phase === 2) {
      // Phase 2: set end point, enter phase 3
      const wall = this.doorPlacement.wall; const wallLen = this.getWallLength(wall);
      const hingeParam = this.doorPlacement.hingeParam; const hingeDist = hingeParam * wallLen;
      const snapThreshold = 1;
      for (const dir of [1, -1]) {
        const doorEndDist = hingeDist + dir * doorWidthFeet;
        const doorEndParam = wallLen > 0 ? doorEndDist / wallLen : 0;
        if (doorEndParam < -0.001 || doorEndParam > 1.001) continue;
        const clampedEndParam = Math.max(0, Math.min(1, doorEndParam));
        // Check overlap and constraints before accepting this snap direction
        const minP = Math.min(hingeParam, clampedEndParam), maxP = Math.max(hingeParam, clampedEndParam);
        if (this._doorsOverlap(wall, this.doorPlacement.room, minP, maxP, null)) continue;
        if (this._doorBlockedByConstraints(wall, minP, maxP, doorWidthFeet)) continue;
        const endPoint = this.paramToPoint(wall, clampedEndParam);
        if (this.getDistance(worldPoint, endPoint) < snapThreshold) {
          this.doorPlacement.endParam = clampedEndParam;
          if (this.doorType === 'roller') {
            // Roller doors skip Phase 3 — no swing
            this.saveUndoState();
            this.doorPlacement.room.doors.push({
              wall: this.doorPlacement.wall,
              hingeParam: this.doorPlacement.hingeParam,
              endParam: clampedEndParam,
              type: 'roller',
              width: this.doorWidth,
              swing: null
            });
            this.doorPlacement = null; this.updateStatus('Roller door placed!'); if (navigator.vibrate) navigator.vibrate(50); this.render(); this.updateMeasurements();
            return;
          }
          this.doorPlacement.phase = 3;
          this.updateStatus('Click to choose swing direction, or click on the door line for a closed door. Esc to go back.');
          this.render(); return;
        }
      }
      this.updateStatus('Move closer to the wall to place the door end point.', { error: true });
    } else if (this.doorPlacement.phase === 3) {
      // Phase 3: choose swing direction
      const swing = this._computeSwingFromClick(worldPoint);
      this.saveUndoState();
      // CUSTOM_ROOMDESIGNER_BY_CLAUDE — on a curve the opening is cut HERE, not on
      // the first click, so an abandoned placement never leaves a hole in the wall.
      if (this.doorPlacement.onCurve) {
        const okCommit = this._commitCurveDoor(
          this.doorPlacement.room, this.doorPlacement.curveId, this.doorPlacement.pendingOpening, swing);
        this.doorPlacement = null;
        this.updateStatus(okCommit ? 'Door placed!' : 'No room for a door here.', okCommit ? undefined : { error: true });
        if (okCommit && navigator.vibrate) navigator.vibrate(50);
        this.render(); this.updateMeasurements();
        return;
      }
      // END CUSTOM_ROOMDESIGNER_BY_CLAUDE
      this.doorPlacement.room.doors.push({
        wall: this.doorPlacement.wall,
        hingeParam: this.doorPlacement.hingeParam,
        endParam: this.doorPlacement.endParam,
        type: this.doorType,
        width: this.doorWidth,
        swing: swing,
        // CUSTOM_ROOMDESIGNER_BY_CLAUDE — set only for doors sitting in a curve opening
        openingId: this.doorPlacement.openingId
      });
      this.doorPlacement = null; this.updateStatus('Door placed!'); if (navigator.vibrate) navigator.vibrate(50); this.render(); this.updateMeasurements();
    }
  }

  _computeSwingFromClick(worldPoint) {
    const dp = this.doorPlacement;
    const wall = dp.wall;
    const hingePoint = this.paramToPoint(wall, dp.hingeParam);
    const endPoint = this.paramToPoint(wall, dp.endParam);
    const midX = (hingePoint.x + endPoint.x) / 2;
    const midY = (hingePoint.y + endPoint.y) / 2;

    // Wall direction and perpendicular
    const wdx = wall.end.x - wall.start.x, wdy = wall.end.y - wall.start.y;
    const wLen = Math.sqrt(wdx * wdx + wdy * wdy);
    if (wLen === 0) return null;
    const wallDirX = wdx / wLen, wallDirY = wdy / wLen;
    const perpX = -wallDirY, perpY = wallDirX;

    // Check if click is very close to the door line itself → closed door
    const dx = worldPoint.x - hingePoint.x, dy = worldPoint.y - hingePoint.y;
    const doorDx = endPoint.x - hingePoint.x, doorDy = endPoint.y - hingePoint.y;
    const doorLen = Math.sqrt(doorDx * doorDx + doorDy * doorDy);
    if (doorLen > 0) {
      const t = (dx * doorDx + dy * doorDy) / (doorLen * doorLen);
      const closestX = hingePoint.x + t * doorDx;
      const closestY = hingePoint.y + t * doorDy;
      const distToLine = Math.sqrt((worldPoint.x - closestX) ** 2 + (worldPoint.y - closestY) ** 2);
      const snapDist = Math.max(0.5, 15 / this.pixelsPerFoot);
      if (distToLine < snapDist && t >= -0.1 && t <= 1.1) return null;
    }

    // Determine perpSign: which side of the wall the click is on
    const toClickX = worldPoint.x - midX, toClickY = worldPoint.y - midY;
    const perpDot = toClickX * perpX + toClickY * perpY;
    const perpSign = perpDot >= 0 ? 1 : -1;

    if (this.doorType === 'double') {
      return { perpSign };
    }

    // For single door: determine hingeEnd based on which end of door is closer to click projection
    const wallDot = toClickX * wallDirX + toClickY * wallDirY;
    const hingeWallDot = (hingePoint.x - midX) * wallDirX + (hingePoint.y - midY) * wallDirY;
    const hingeEnd = (wallDot * hingeWallDot >= 0) ? 'hinge' : 'end';

    return { perpSign, hingeEnd };
  }

  _getSwingOptions(doorType) { return RoomDesignerTools.getSwingOptions(doorType || this.doorType); }

  _computeSwingForDoor(door, worldPoint) {
    const wall = door.wall;
    const hingePoint = this.paramToPoint(wall, door.hingeParam);
    const endPoint = this.paramToPoint(wall, door.endParam);
    const midX = (hingePoint.x + endPoint.x) / 2, midY = (hingePoint.y + endPoint.y) / 2;
    const wdx = wall.end.x - wall.start.x, wdy = wall.end.y - wall.start.y;
    const wLen = Math.sqrt(wdx * wdx + wdy * wdy);
    if (wLen === 0) return null;
    const wallDirX = wdx / wLen, wallDirY = wdy / wLen;
    const perpX = -wallDirY, perpY = wallDirX;
    // Check if click is on the door line → closed
    const dx = worldPoint.x - hingePoint.x, dy = worldPoint.y - hingePoint.y;
    const doorDx = endPoint.x - hingePoint.x, doorDy = endPoint.y - hingePoint.y;
    const doorLen = Math.sqrt(doorDx * doorDx + doorDy * doorDy);
    if (doorLen > 0) {
      const t = (dx * doorDx + dy * doorDy) / (doorLen * doorLen);
      const closestX = hingePoint.x + t * doorDx, closestY = hingePoint.y + t * doorDy;
      const distToLine = Math.sqrt((worldPoint.x - closestX) ** 2 + (worldPoint.y - closestY) ** 2);
      const snapDist = Math.max(0.5, 15 / this.pixelsPerFoot);
      if (distToLine < snapDist && t >= -0.1 && t <= 1.1) return null;
    }
    const toClickX = worldPoint.x - midX, toClickY = worldPoint.y - midY;
    const perpDot = toClickX * perpX + toClickY * perpY;
    const perpSign = perpDot >= 0 ? 1 : -1;
    if (door.type === 'double') return { perpSign };
    const wallDot = toClickX * wallDirX + toClickY * wallDirY;
    const hingeWallDot = (hingePoint.x - midX) * wallDirX + (hingePoint.y - midY) * wallDirY;
    const hingeEnd = (wallDot * hingeWallDot >= 0) ? 'hinge' : 'end';
    return { perpSign, hingeEnd };
  }

  _drawDoorSwingArc(hingePoint, endPoint, doorWidth, wallDirX, wallDirY, perpX, perpY, perpSign, hingeEnd, doorType, scale, color, alpha) {
    // Renders swing leaf + quarter-circle arc for one swing configuration
    if (doorType === 'roller') { return; }
    this.ctx.save();
    this.ctx.globalAlpha = alpha;
    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = 1 / scale;

    if (doorType === 'double') {
      const halfW = doorWidth / 2;

      // Left leaf from hinge
      const leafEndLX = hingePoint.x + perpX * perpSign * halfW;
      const leafEndLY = hingePoint.y + perpY * perpSign * halfW;
      this.ctx.beginPath();
      this.ctx.moveTo(hingePoint.x, hingePoint.y);
      this.ctx.lineTo(leafEndLX, leafEndLY);
      this.ctx.stroke();

      // Left arc: from closed (toward mid) to open (perpendicular)
      const midX = (hingePoint.x + endPoint.x) / 2;
      const midY = (hingePoint.y + endPoint.y) / 2;
      const angleClosedL = Math.atan2(midY - hingePoint.y, midX - hingePoint.x);
      const angleOpenL = Math.atan2(perpY * perpSign, perpX * perpSign);
      let diffL = angleOpenL - angleClosedL;
      while (diffL > Math.PI) diffL -= 2 * Math.PI;
      while (diffL < -Math.PI) diffL += 2 * Math.PI;
      this.ctx.beginPath();
      this.ctx.arc(hingePoint.x, hingePoint.y, halfW, angleClosedL, angleOpenL, diffL < 0);
      this.ctx.stroke();

      // Right leaf from end
      const leafEndRX = endPoint.x + perpX * perpSign * halfW;
      const leafEndRY = endPoint.y + perpY * perpSign * halfW;
      this.ctx.beginPath();
      this.ctx.moveTo(endPoint.x, endPoint.y);
      this.ctx.lineTo(leafEndRX, leafEndRY);
      this.ctx.stroke();

      // Right arc: from closed (toward mid) to open (perpendicular)
      const angleClosedR = Math.atan2(midY - endPoint.y, midX - endPoint.x);
      const angleOpenR = Math.atan2(perpY * perpSign, perpX * perpSign);
      let diffR = angleOpenR - angleClosedR;
      while (diffR > Math.PI) diffR -= 2 * Math.PI;
      while (diffR < -Math.PI) diffR += 2 * Math.PI;
      this.ctx.beginPath();
      this.ctx.arc(endPoint.x, endPoint.y, halfW, angleClosedR, angleOpenR, diffR < 0);
      this.ctx.stroke();
    } else {
      // Single door
      const pivotPt = hingeEnd === 'hinge' ? hingePoint : endPoint;
      const otherPt = hingeEnd === 'hinge' ? endPoint : hingePoint;

      // Leaf line from pivot perpendicular to wall
      const leafEndX = pivotPt.x + perpX * perpSign * doorWidth;
      const leafEndY = pivotPt.y + perpY * perpSign * doorWidth;
      this.ctx.beginPath();
      this.ctx.moveTo(pivotPt.x, pivotPt.y);
      this.ctx.lineTo(leafEndX, leafEndY);
      this.ctx.stroke();

      // Arc from closed (toward other end) to open (perpendicular)
      const closedAngle = Math.atan2(otherPt.y - pivotPt.y, otherPt.x - pivotPt.x);
      const openAngle = Math.atan2(perpY * perpSign, perpX * perpSign);
      let diff = openAngle - closedAngle;
      while (diff > Math.PI) diff -= 2 * Math.PI;
      while (diff < -Math.PI) diff += 2 * Math.PI;
      this.ctx.beginPath();
      this.ctx.arc(pivotPt.x, pivotPt.y, doorWidth, closedAngle, openAngle, diff < 0);
      this.ctx.stroke();
    }
    this.ctx.restore();
  }

  _drawDoorPreview(scale) {
    // Rectangle/circle preview — just show ghost at cursor
    if (this.objectType === 'rectangle' || this.objectType === 'circle') {
      const snapped = this.snapToGrid(this.mouseWorldRaw.x, this.mouseWorldRaw.y);
      this.ctx.globalAlpha = this._isMobile ? 0.55 : 0.35;
      this.ctx.strokeStyle = '#006BCC';
      this.ctx.fillStyle = this._tc.previewFill;
      this.ctx.lineWidth = 2 / scale;
      if (this.objectType === 'rectangle') {
        const w = this.rectWidth / 12, d = this.rectDepth / 12;
        this.ctx.fillRect(snapped.x - w / 2, snapped.y - d / 2, w, d);
        this.ctx.strokeRect(snapped.x - w / 2, snapped.y - d / 2, w, d);
      } else {
        const r = (this.circleDiameter / 12) / 2;
        this.ctx.beginPath();
        this.ctx.arc(snapped.x, snapped.y, r, 0, Math.PI * 2);
        this.ctx.fill();
        this.ctx.stroke();
      }
      // Placement dimension lines to nearest walls + aligned objects
      this.ctx.globalAlpha = 1.0;
      this._drawObjectPlacementDimensions(snapped, scale);
      // Snap cursor dot
      this.ctx.globalAlpha = this._isMobile ? 0.8 : 0.6;
      this.ctx.fillStyle = '#006BCC';
      this.ctx.beginPath();
      this.ctx.arc(snapped.x, snapped.y, 3 / scale, 0, Math.PI * 2);
      this.ctx.fill();
      this.ctx.globalAlpha = 1.0;
      return;
    }

    const doorWidthFeet = this.doorWidth / 12;
    if (!this.doorPlacement) {
      // Mobile door Phase 0/1: centered door preview (no flip behavior)
      if (this._doorMobile && this._doorMobile.phase <= 1) {
        const dm = this._doorMobile;
        const wall = dm.wall;
        const wallLen = this.getWallLength(wall);
        const halfParam = wallLen > 0 ? (doorWidthFeet / 2) / wallLen : 0.5;
        const startParam = Math.max(0, dm.param - halfParam);
        const endParam = Math.min(1, dm.param + halfParam);
        const startPt = this.paramToPoint(wall, startParam);
        const endPt = this.paramToPoint(wall, endParam);
        const centerPt = this.paramToPoint(wall, dm.param);
        const wdx = wall.end.x - wall.start.x, wdy = wall.end.y - wall.start.y;
        const wLen = Math.sqrt(wdx * wdx + wdy * wdy);
        const perpX = wLen > 0 ? -wdy / wLen : 0, perpY = wLen > 0 ? wdx / wLen : 0;
        const wallDirX = wLen > 0 ? wdx / wLen : 1, wallDirY = wLen > 0 ? wdy / wLen : 0;
        const off1 = this._getDoorOffset(wall, perpX, perpY, dm.room);
        // Check if door can fit (wall long enough + no overlap + side/junction constraints)
        const wallTooShort = wallLen < doorWidthFeet - 0.01;
        const overlaps = !wallTooShort && this._doorsOverlap(wall, dm.room, startParam, endParam, null);
        const blocked = !wallTooShort && !overlaps && this._doorBlockedByConstraints(wall, startParam, endParam, doorWidthFeet);
        const canPlace = !wallTooShort && !overlaps && !blocked;
        const dotColor = canPlace ? 'rgba(0, 200, 0, 0.6)' : 'rgba(204, 0, 0, 0.6)';
        // Gap measurements on both sides — each side only sees its relevant break points
        const mobileBreaks = this._getWallBreakPoints(wall);
        const fontSize = (11 + Math.min(7, Math.max(0, (scale - 20) / 12))) / scale;
        const labelOffset = Math.max(this.wallThickness * 2.5, 22 / scale);
        const dimPerp = Math.max(this.wallThickness * 2.0, 16 / scale);
        const nubLen = Math.max(this.wallThickness * 0.4, 4 / scale);
        const drawMobileDoorDim = (fromParam, bp, pnx, pny) => {
          const adjP = bp.adjust > 0
            ? (bp.param < fromParam ? bp.param + bp.adjust / wallLen : bp.param - bp.adjust / wallLen)
            : bp.param;
          const lo = Math.min(adjP, fromParam), hi = Math.max(adjP, fromParam);
          const dist = (hi - lo) * wallLen;
          if (dist < 0.01) return;
          const p1 = this.paramToPoint(wall, fromParam);
          const p2 = this.paramToPoint(wall, adjP);
          const ox = pnx * dimPerp, oy = pny * dimPerp;
          const x1 = p1.x + off1.x + ox, y1 = p1.y + off1.y + oy;
          const x2 = p2.x + off1.x + ox, y2 = p2.y + off1.y + oy;
          this.ctx.strokeStyle = 'rgba(0, 107, 204, 0.55)';
          this.ctx.lineWidth = 1.5 / scale;
          this.ctx.setLineDash([2 / scale, 2 / scale]);
          this.ctx.beginPath(); this.ctx.moveTo(x1, y1); this.ctx.lineTo(x2, y2); this.ctx.stroke();
          this.ctx.setLineDash([]);
          this.ctx.beginPath();
          this.ctx.moveTo(x1 - pnx * nubLen, y1 - pny * nubLen); this.ctx.lineTo(x1 + pnx * nubLen, y1 + pny * nubLen);
          const bpIsEnd = bp.param < 0.02 || bp.param > 0.98;
          if (bpIsEnd) { this.ctx.moveTo(x2, y2); this.ctx.lineTo(x2 + pnx * nubLen, y2 + pny * nubLen); }
          else { this.ctx.moveTo(x2 - pnx * nubLen, y2 - pny * nubLen); this.ctx.lineTo(x2 + pnx * nubLen, y2 + pny * nubLen); }
          this.ctx.stroke();
          this.ctx.font = `${fontSize}px Arial`; this.ctx.fillStyle = '#006BCC';
          this.ctx.textAlign = 'center'; this.ctx.textBaseline = 'middle';
          const lblDist = Math.min(dist * 0.3, 30 / scale);
          const lblParam = bp.param < fromParam ? fromParam - lblDist / wallLen : fromParam + lblDist / wallLen;
          const lblPt = this.paramToPoint(wall, Math.max(0, Math.min(1, lblParam)));
          this.ctx.fillText(this.formatMeasurement(dist), lblPt.x + pnx * labelOffset, lblPt.y + pny * labelOffset);
        };
        // For room walls, only draw on the interior side (skip exterior)
        let mobDoorIntSign = 0;
        if (dm.room && dm.room.closed) {
          const geo = this._getRoomGeoCache(dm.room);
          if (geo && geo.roomPolygon && geo.roomPolygon.length >= 3) {
            const midP = this.paramToPoint(wall, 0.5);
            const testDist = 0.1;
            const posInside = this.pointInPolygon(midP.x + perpX * testDist, midP.y + perpY * testDist, geo.roomPolygon);
            mobDoorIntSign = posInside ? 1 : -1;
          }
        }
        for (const sign of [1, -1]) {
          if (mobDoorIntSign !== 0 && sign !== mobDoorIntSign) continue;
          const pnx = perpX * sign, pny = perpY * sign;
          const sideKey = sign === 1 ? 'pos' : 'neg';
          const sideBPs = mobileBreaks.filter(bp => bp.sides === sideKey || bp.sides === 'both');
          let sideLeft = null, sideRight = null;
          for (const bp of sideBPs) { if (bp.param < startParam - 0.005) sideLeft = bp; }
          for (const bp of sideBPs) { if (bp.param > endParam + 0.005) { sideRight = bp; break; } }
          if (sideLeft) drawMobileDoorDim(startParam, sideLeft, pnx, pny);
          if (sideRight) drawMobileDoorDim(endParam, sideRight, pnx, pny);
        }
        this._drawDoorToNearestDoorDimension(wall, dm.room, startParam, endParam, perpX, perpY, labelOffset, off1, scale);
        this.ctx.textAlign = 'start'; this.ctx.textBaseline = 'alphabetic';
        // Draw wall gap + door graphic
        const offStart = { x: startPt.x + off1.x, y: startPt.y + off1.y };
        const offEnd = { x: endPt.x + off1.x, y: endPt.y + off1.y };
        const gapEraseWidth = this.wallThickness + 3 / scale;
        this._clearDoorGap(offStart, offEnd, gapEraseWidth, scale);
        this.drawDoorGraphic(offStart, offEnd, wallDirX, wallDirY, perpX, perpY, this.doorType, scale, '#006BCC', 0.35);
        // Draw centered green dot with intro animation
        let doorDotR = 4 / scale, doorDotAlpha = 1.0;
        if (this._rectIntroAnimating) {
          const elapsed = performance.now() - this._rectIntroAnimStart;
          const t = Math.min(elapsed / 400, 1);
          const dotScale = t < 0.6 ? (t / 0.6 * 1.15) : (1.15 - 0.15 * ((t - 0.6) / 0.4));
          doorDotR = (6 / scale) * dotScale;
          doorDotAlpha = Math.min(elapsed / 150, 1);
        }
        this.ctx.globalAlpha = doorDotAlpha;
        this.ctx.fillStyle = dotColor; this.ctx.beginPath(); this.ctx.arc(centerPt.x + off1.x, centerPt.y + off1.y, doorDotR, 0, Math.PI * 2); this.ctx.fill();
        this.ctx.globalAlpha = 1.0;
      } else {
      // Desktop: hinge-based preview with flip behavior
      const nearest = this.findNearestWall(this.mouseWorldRaw, 2);
      // CUSTOM_ROOMDESIGNER_BY_CLAUDE — on a curve the preview has to show the OPENING
      // that would be cut, not measure the ~10in facet against the door width. Without
      // this the length gate below skips the entire preview block, so hovering a curved
      // wall gives no ghost and no dot and the tool looks like it is refusing to place
      // a door there — even though the click path handles it fine.
      if (nearest && nearest.wall.curveId) {
        this._drawCurveDoorPreview(nearest, scale, doorWidthFeet);
      } else
      // END CUSTOM_ROOMDESIGNER_BY_CLAUDE
      if (nearest && this.getWallLength(nearest.wall) >= doorWidthFeet - 0.01) {
        // CUSTOM_ROOMDESIGNER_BY_CLAUDE — single-click centred placement, same as the
        // curved-wall flow. The door is centred on the cursor with the near jamb
        // snapped to the grid; the old hinge-then-direction second click is gone,
        // because a centred door has no direction left to choose. Which end HINGES is
        // still chosen by the swing click, via _computeSwingFromClick's hingeEnd.
        const wall = nearest.wall;
        const wallLen = this.getWallLength(wall);
        const span = this._doorSpanOnWall(wall, this.mouseWorldRaw, doorWidthFeet, nearest.room);
        const snappedParam = span.loP;
        let doorEndParam = span.hiP;
        const snapPoint = this.paramToPoint(wall, snappedParam);
        const centrePoint = this.paramToPoint(wall, (span.loP + span.hiP) / 2);
        const paramWidth = wallLen > 0 ? doorWidthFeet / wallLen : 1;
        const dotColor = span.canPlace ? 'rgba(0, 200, 0, 0.6)' : 'rgba(204, 0, 0, 0.6)';
        // END CUSTOM_ROOMDESIGNER_BY_CLAUDE
        const wdx = wall.end.x - wall.start.x, wdy = wall.end.y - wall.start.y, wLen = Math.sqrt(wdx * wdx + wdy * wdy);
        const perpX = wLen > 0 ? -wdy / wLen : 0, perpY = wLen > 0 ? wdx / wLen : 0;
        const off1 = this._getDoorOffset(wall, perpX, perpY, nearest.room);
        // Measure from each door edge to the nearest break point on both sides
        const doorMinParam = Math.min(snappedParam, doorEndParam);
        const doorMaxParam = Math.max(snappedParam, doorEndParam);
        const doorBreaks = this._getWallBreakPoints(wall);
        const fontSize = (11 + Math.min(7, Math.max(0, (scale - 20) / 12))) / scale;
        const labelOffset = Math.max(this.wallThickness * 2.5, 22 / scale);
        const dimPerp = Math.max(this.wallThickness * 2.0, 16 / scale);
        const nubLen = Math.max(this.wallThickness * 0.4, 4 / scale);
        const drawDoorDim = (fromParam, bp, pnx, pny) => {
          const adjP = bp.adjust > 0
            ? (bp.param < fromParam ? bp.param + bp.adjust / wallLen : bp.param - bp.adjust / wallLen)
            : bp.param;
          const lo = Math.min(adjP, fromParam), hi = Math.max(adjP, fromParam);
          const dist = (hi - lo) * wallLen;
          if (dist < 0.01) return;
          const p1 = this.paramToPoint(wall, fromParam);
          const p2 = this.paramToPoint(wall, adjP);
          const ox = pnx * dimPerp, oy = pny * dimPerp;
          const x1 = p1.x + off1.x + ox, y1 = p1.y + off1.y + oy;
          const x2 = p2.x + off1.x + ox, y2 = p2.y + off1.y + oy;
          this.ctx.strokeStyle = 'rgba(0, 107, 204, 0.55)';
          this.ctx.lineWidth = 1.5 / scale;
          this.ctx.setLineDash([2 / scale, 2 / scale]);
          this.ctx.beginPath(); this.ctx.moveTo(x1, y1); this.ctx.lineTo(x2, y2); this.ctx.stroke();
          this.ctx.setLineDash([]);
          this.ctx.beginPath();
          this.ctx.moveTo(x1 - pnx * nubLen, y1 - pny * nubLen); this.ctx.lineTo(x1 + pnx * nubLen, y1 + pny * nubLen);
          const bpIsEnd = bp.param < 0.02 || bp.param > 0.98;
          if (bpIsEnd) { this.ctx.moveTo(x2, y2); this.ctx.lineTo(x2 + pnx * nubLen, y2 + pny * nubLen); }
          else { this.ctx.moveTo(x2 - pnx * nubLen, y2 - pny * nubLen); this.ctx.lineTo(x2 + pnx * nubLen, y2 + pny * nubLen); }
          this.ctx.stroke();
          this.ctx.font = `${fontSize}px Arial`; this.ctx.fillStyle = '#006BCC';
          this.ctx.textAlign = 'center'; this.ctx.textBaseline = 'middle';
          const midPt = this.paramToPoint(wall, (lo + hi) / 2);
          this.ctx.fillText(this.formatMeasurement(dist), midPt.x + pnx * labelOffset, midPt.y + pny * labelOffset);
        };
        // For room walls, only draw on the interior side (skip exterior)
        let deskDoorIntSign = 0;
        if (nearest.room && nearest.room.closed) {
          const geo = this._getRoomGeoCache(nearest.room);
          if (geo && geo.roomPolygon && geo.roomPolygon.length >= 3) {
            const midP = this.paramToPoint(wall, 0.5);
            const testDist = 0.1;
            const posInside = this.pointInPolygon(midP.x + perpX * testDist, midP.y + perpY * testDist, geo.roomPolygon);
            deskDoorIntSign = posInside ? 1 : -1;
          }
        }
        for (const sign of [1, -1]) {
          if (deskDoorIntSign !== 0 && sign !== deskDoorIntSign) continue;
          const pnx = perpX * sign, pny = perpY * sign;
          const sideKey = sign === 1 ? 'pos' : 'neg';
          const sideBPs = doorBreaks.filter(bp => bp.sides === sideKey || bp.sides === 'both');
          let sideLeft = null, sideRight = null;
          for (const bp of sideBPs) { if (bp.param < doorMinParam - 0.005) sideLeft = bp; }
          for (const bp of sideBPs) { if (bp.param > doorMaxParam + 0.005) { sideRight = bp; break; } }
          if (sideLeft) drawDoorDim(doorMinParam, sideLeft, pnx, pny);
          if (sideRight) drawDoorDim(doorMaxParam, sideRight, pnx, pny);
        }
        this._drawDoorToNearestDoorDimension(wall, nearest.room, doorMinParam, doorMaxParam, perpX, perpY, labelOffset, off1, scale);
        this.ctx.textAlign = 'start'; this.ctx.textBaseline = 'alphabetic';
        if (doorEndParam >= 0 && doorEndParam <= 1) {
          const endPt = this.paramToPoint(wall, doorEndParam);
          const wallDirX = wLen > 0 ? wdx / wLen : 1, wallDirY = wLen > 0 ? wdy / wLen : 0;
          // Draw wall gap (erase wall section under door preview)
          const gapEraseWidth = this.wallThickness + 3 / scale;
          const offSnap1 = { x: snapPoint.x + off1.x, y: snapPoint.y + off1.y };
          const offEnd1 = { x: endPt.x + off1.x, y: endPt.y + off1.y };
          this._clearDoorGap(offSnap1, offEnd1, gapEraseWidth, scale);
          this.drawDoorGraphic(offSnap1, offEnd1, wallDirX, wallDirY, perpX, perpY, this.doorType, scale, '#006BCC', 0.35);
        }
        // Placement dot sits on the door's CENTRE now, since that is what follows the
        // cursor. CUSTOM_ROOMDESIGNER_BY_CLAUDE
        this.ctx.fillStyle = dotColor; this.ctx.beginPath(); this.ctx.arc(centrePoint.x + off1.x, centrePoint.y + off1.y, 4 / scale, 0, Math.PI * 2); this.ctx.fill();
      } else {
        const mx = this.mouseWorldRaw.x, my = this.mouseWorldRaw.y, halfWidth = doorWidthFeet / 2;
        this.drawDoorGraphic({ x: mx - halfWidth, y: my }, { x: mx + halfWidth, y: my }, 1, 0, 0, -1, this.doorType, scale, '#006BCC', 0.2);
      }
      }
    } else if (this.doorPlacement.phase === 2) {
      // Phase 2: show end point snap targets / hinge selection
      const wall = this.doorPlacement.wall; const hingePoint = this.doorPlacement.hingePoint;
      const hingeParam = this.doorPlacement.hingeParam; const wallLen = this.getWallLength(wall);
      const wdx2 = wall.end.x - wall.start.x, wdy2 = wall.end.y - wall.start.y, wLen2 = Math.sqrt(wdx2 * wdx2 + wdy2 * wdy2);
      const ph2perpX = wLen2 > 0 ? -wdy2 / wLen2 : 0, ph2perpY = wLen2 > 0 ? wdx2 / wLen2 : 0;
      const ph2Off = this._getDoorOffset(wall, ph2perpX, ph2perpY, this.doorPlacement.room);
      const wallDirX2 = wLen2 > 0 ? wdx2 / wLen2 : 1, wallDirY2 = wLen2 > 0 ? wdy2 / wLen2 : 0;
      if (this._doorMobile && this._doorMobile.phase === 2) {
        // Mobile Phase 2: both ends are fixed, show door with hinge selection dots
        const dm = this._doorMobile;
        const halfParam = wallLen > 0 ? (doorWidthFeet / 2) / wallLen : 0.5;
        const leftParam = dm.param - halfParam;
        const rightParam = dm.param + halfParam;
        const leftPt = this.paramToPoint(wall, leftParam);
        const rightPt = this.paramToPoint(wall, rightParam);
        const offLeft = { x: leftPt.x + ph2Off.x, y: leftPt.y + ph2Off.y };
        const offRight = { x: rightPt.x + ph2Off.x, y: rightPt.y + ph2Off.y };
        // Draw wall gap + door graphic
        const gapEraseWidth = this.wallThickness + 3 / scale;
        this._clearDoorGap(offLeft, offRight, gapEraseWidth, scale);
        this.drawDoorGraphic(offLeft, offRight, wallDirX2, wallDirY2, ph2perpX, ph2perpY, this.doorType, scale, '#006BCC', 0.5);
        // Draw hinge selection dots at both ends
        const leftSelected = dm.endDir === -1; // -1 = hinge at left (toward wall.start)
        const rightSelected = dm.endDir === 1;  // +1 = hinge at right (toward wall.end)
        const dotR = 10 / scale;
        // Left dot
        this.ctx.fillStyle = leftSelected ? 'rgba(0, 200, 0, 0.8)' : 'rgba(0, 107, 204, 0.4)';
        this.ctx.beginPath(); this.ctx.arc(offLeft.x, offLeft.y, leftSelected ? dotR : dotR * 0.7, 0, Math.PI * 2); this.ctx.fill();
        // Right dot
        this.ctx.fillStyle = rightSelected ? 'rgba(0, 200, 0, 0.8)' : 'rgba(0, 107, 204, 0.4)';
        this.ctx.beginPath(); this.ctx.arc(offRight.x, offRight.y, rightSelected ? dotR : dotR * 0.7, 0, Math.PI * 2); this.ctx.fill();
      } else {
        // Desktop: rotating preview + end point snap targets
        const dx = this.mouseWorldRaw.x - hingePoint.x, dy = this.mouseWorldRaw.y - hingePoint.y;
        const angle = Math.atan2(dy, dx);
        const doorEnd = { x: hingePoint.x + Math.cos(angle) * doorWidthFeet, y: hingePoint.y + Math.sin(angle) * doorWidthFeet };
        this.drawDoorGraphic(hingePoint, doorEnd, Math.cos(angle), Math.sin(angle), -Math.sin(angle), Math.cos(angle), this.doorType, scale, '#006BCC', 0.25);
        this.ctx.fillStyle = 'rgba(0, 200, 0, 0.7)'; this.ctx.beginPath(); this.ctx.arc(hingePoint.x + ph2Off.x, hingePoint.y + ph2Off.y, 4 / scale, 0, Math.PI * 2); this.ctx.fill();
        const hingeDist = hingeParam * wallLen;
        for (const dir of [1, -1]) {
          const doorEndDist = hingeDist + dir * doorWidthFeet;
          const doorEndParam = wallLen > 0 ? doorEndDist / wallLen : 0;
          if (doorEndParam < -0.001 || doorEndParam > 1.001) continue;
          const clampedEndParam = Math.max(0, Math.min(1, doorEndParam));
          const minP = Math.min(hingeParam, clampedEndParam), maxP = Math.max(hingeParam, clampedEndParam);
          const overlaps = this._doorsOverlap(wall, this.doorPlacement.room, minP, maxP, null);
          const constrained = !overlaps && this._doorBlockedByConstraints(wall, minP, maxP, doorWidthFeet);
          const blocked = overlaps || constrained;
          const snapEndPoint = this.paramToPoint(wall, clampedEndParam);
          const distToSnap = this.getDistance(this.mouseWorldRaw, snapEndPoint);
          const isNear = this._isMobile ? true : distToSnap < 1;
          if (blocked) {
            this.ctx.fillStyle = isNear ? 'rgba(204, 0, 0, 0.7)' : 'rgba(204, 0, 0, 0.3)';
          } else {
            this.ctx.fillStyle = isNear ? 'rgba(0, 200, 0, 0.7)' : 'rgba(0, 107, 204, 0.3)';
          }
          const dotR = (this._isMobile ? 10 : (isNear ? 8 : 5)) / scale;
          this.ctx.beginPath(); this.ctx.arc(snapEndPoint.x + ph2Off.x, snapEndPoint.y + ph2Off.y, dotR, 0, Math.PI * 2); this.ctx.fill();
          if (isNear && !blocked) {
            this.drawDoorGraphic({ x: hingePoint.x + ph2Off.x, y: hingePoint.y + ph2Off.y }, { x: snapEndPoint.x + ph2Off.x, y: snapEndPoint.y + ph2Off.y }, wallDirX2, wallDirY2, ph2perpX, ph2perpY, this.doorType, scale, '#006BCC', 0.6);
          }
        }
      }
    } else if (this.doorPlacement.phase === 3) {
      // Phase 3: show swing options
      const wall = this.doorPlacement.wall;
      const hingePoint = this.paramToPoint(wall, this.doorPlacement.hingeParam);
      const endPoint = this.paramToPoint(wall, this.doorPlacement.endParam);
      const wdx = wall.end.x - wall.start.x, wdy = wall.end.y - wall.start.y;
      const wLen = Math.sqrt(wdx * wdx + wdy * wdy);
      if (wLen === 0) return;
      const wallDirX = wdx / wLen, wallDirY = wdy / wLen;
      const perpX = -wallDirY, perpY = wallDirX;
      const off3 = this._getDoorOffset(wall, perpX, perpY, this.doorPlacement.room);
      const offHinge3 = { x: hingePoint.x + off3.x, y: hingePoint.y + off3.y };
      const offEnd3 = { x: endPoint.x + off3.x, y: endPoint.y + off3.y };

      // Draw wall gap (centered on wall, wallThickness wide)
      const gapEraseWidth2 = this.wallThickness + 3 / scale;
      this._clearDoorGap(offHinge3, offEnd3, gapEraseWidth2, scale);

      // Draw solid door frame (tick marks + leaf rectangles)
      this.drawDoorGraphic(offHinge3, offEnd3, wallDirX, wallDirY, perpX, perpY, this.doorType, scale, '#006BCC', 0.7);

      // Draw all ghost swing options
      const options = this._getSwingOptions();
      const midX = (offHinge3.x + offEnd3.x) / 2;
      const midY = (offHinge3.y + offEnd3.y) / 2;

      // Determine which option to highlight
      let bestIdx = -1, closedHighlighted = false;
      if (this._doorMobile && this._doorMobile.phase === 3) {
        // Mobile: highlight based on selected swingIdx
        bestIdx = this._doorMobile.swingIdx != null ? this._doorMobile.swingIdx : -1;
        closedHighlighted = (this._doorMobile.swingOption === null && bestIdx === -1);
      } else {
        // Desktop: highlight based on cursor proximity
        let bestDist = Infinity;
        const mx = this.mouseWorldRaw.x, my = this.mouseWorldRaw.y;
        const doorDx = offEnd3.x - offHinge3.x, doorDy = offEnd3.y - offHinge3.y;
        const doorLen = Math.sqrt(doorDx * doorDx + doorDy * doorDy);
        let closedDist = Infinity;
        if (doorLen > 0) {
          const t = ((mx - offHinge3.x) * doorDx + (my - offHinge3.y) * doorDy) / (doorLen * doorLen);
          const clampT = Math.max(0, Math.min(1, t));
          const closestX = offHinge3.x + clampT * doorDx;
          const closestY = offHinge3.y + clampT * doorDy;
          closedDist = Math.sqrt((mx - closestX) ** 2 + (my - closestY) ** 2);
        }
        for (let i = 0; i < options.length; i++) {
          const opt = options[i];
          let tipX, tipY;
          if (this.doorType === 'double') {
            tipX = midX + perpX * opt.perpSign * (doorWidthFeet / 2);
            tipY = midY + perpY * opt.perpSign * (doorWidthFeet / 2);
          } else {
            const pivotPt = opt.hingeEnd === 'hinge' ? offHinge3 : offEnd3;
            tipX = pivotPt.x + perpX * opt.perpSign * doorWidthFeet;
            tipY = pivotPt.y + perpY * opt.perpSign * doorWidthFeet;
          }
          const dist = Math.sqrt((mx - tipX) ** 2 + (my - tipY) ** 2);
          if (dist < bestDist) { bestDist = dist; bestIdx = i; }
        }
        const snapDist = Math.max(0.5, 15 / this.pixelsPerFoot);
        closedHighlighted = closedDist < snapDist && closedDist < bestDist;
        if (closedHighlighted) bestIdx = -1;
      }

      for (let i = 0; i < options.length; i++) {
        const opt = options[i];
        const highlighted = (i === bestIdx);
        const alpha = highlighted ? 0.45 : 0.15;

        if (this.doorType === 'double') {
          this._drawDoorSwingArc(offHinge3, offEnd3, doorWidthFeet, wallDirX, wallDirY, perpX, perpY, opt.perpSign, null, 'double', scale, '#006BCC', alpha);
        } else {
          this._drawDoorSwingArc(offHinge3, offEnd3, doorWidthFeet, wallDirX, wallDirY, perpX, perpY, opt.perpSign, opt.hingeEnd, 'single', scale, '#006BCC', alpha);
        }

        // Draw dot at swing tip
        let tipX, tipY;
        if (this.doorType === 'double') {
          tipX = midX + perpX * opt.perpSign * (doorWidthFeet / 2);
          tipY = midY + perpY * opt.perpSign * (doorWidthFeet / 2);
        } else {
          const pivotPt = opt.hingeEnd === 'hinge' ? offHinge3 : offEnd3;
          tipX = pivotPt.x + perpX * opt.perpSign * doorWidthFeet;
          tipY = pivotPt.y + perpY * opt.perpSign * doorWidthFeet;
        }
        this.ctx.fillStyle = highlighted ? 'rgba(0, 200, 0, 0.7)' : 'rgba(0, 107, 204, 0.3)';
        const swDotR = this._isMobile ? 8 : (highlighted ? 8 : 5);
        this.ctx.beginPath();
        this.ctx.arc(tipX, tipY, swDotR / scale, 0, Math.PI * 2);
        this.ctx.fill();
      }

      // Draw dot at door midpoint for "closed" option
      const closedDotX = (offHinge3.x + offEnd3.x) / 2;
      const closedDotY = (offHinge3.y + offEnd3.y) / 2;
      this.ctx.fillStyle = closedHighlighted ? 'rgba(0, 200, 0, 0.7)' : 'rgba(0, 107, 204, 0.3)';
      const closedDotR = this._isMobile ? 8 : (closedHighlighted ? 8 : 5);
      this.ctx.beginPath();
      this.ctx.arc(closedDotX, closedDotY, closedDotR / scale, 0, Math.PI * 2);
      this.ctx.fill();

      // Show "closed" indicator if cursor is near the door line
      if (closedHighlighted) {
        this.ctx.save();
        this.ctx.strokeStyle = '#006BCC';
        this.ctx.lineWidth = 3 / scale;
        this.ctx.globalAlpha = 0.6;
        this.ctx.beginPath();
        this.ctx.moveTo(offHinge3.x, offHinge3.y);
        this.ctx.lineTo(offEnd3.x, offEnd3.y);
        this.ctx.stroke();
        this.ctx.restore();
      }
    }
  }

  _drawDoorToNearestDoorDimension(wall, room, previewMinParam, previewMaxParam, perpX, perpY, labelOffset, doorOffset, scale) {
    if (!room || !room.doors || room.doors.length === 0) return;
    const wallLen = this.getWallLength(wall);
    if (wallLen < 0.01) return;

    let nearestGap = Infinity, nearestPt1 = null, nearestPt2 = null;
    for (const door of room.doors) {
      if (door.wall !== wall) continue;
      const dMin = Math.min(door.hingeParam, door.endParam);
      const dMax = Math.max(door.hingeParam, door.endParam);
      // Gap between preview right edge and door left edge
      const gap1 = (dMin - previewMaxParam) * wallLen;
      // Gap between door right edge and preview left edge
      const gap2 = (previewMinParam - dMax) * wallLen;
      if (gap1 > 0.01 && gap1 < nearestGap) {
        nearestGap = gap1;
        nearestPt1 = this.paramToPoint(wall, previewMaxParam);
        nearestPt2 = this.paramToPoint(wall, dMin);
      }
      if (gap2 > 0.01 && gap2 < nearestGap) {
        nearestGap = gap2;
        nearestPt1 = this.paramToPoint(wall, previewMinParam);
        nearestPt2 = this.paramToPoint(wall, dMax);
      }
    }
    if (!nearestPt1 || nearestGap === Infinity) return;

    // Offset points to wall center (same as door graphic offset)
    const p1 = { x: nearestPt1.x + doorOffset.x, y: nearestPt1.y + doorOffset.y };
    const p2 = { x: nearestPt2.x + doorOffset.x, y: nearestPt2.y + doorOffset.y };

    // Draw dotted dimension line
    this.ctx.strokeStyle = 'rgba(0, 107, 204, 0.55)';
    this.ctx.lineWidth = 1.5 / scale;
    this.ctx.setLineDash([2 / scale, 2 / scale]);
    this.ctx.beginPath();
    this.ctx.moveTo(p1.x, p1.y);
    this.ctx.lineTo(p2.x, p2.y);
    this.ctx.stroke();
    this.ctx.setLineDash([]);

    // Label offset to OPPOSITE side of wall from door-to-wall labels
    // Mobile: keep close to the preview door edge (p1); desktop: midpoint
    const fontSize = (11 + Math.min(7, Math.max(0, (scale - 20) / 12))) / scale;
    this.ctx.font = `${fontSize}px Arial`;
    this.ctx.fillStyle = '#006BCC';
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';
    const lblT = this._isMobile ? Math.min(0.3, (30 / scale) / Math.max(0.01, nearestGap)) : 0.5;
    const lblX = p1.x + (p2.x - p1.x) * lblT;
    const lblY = p1.y + (p2.y - p1.y) * lblT;
    this.ctx.fillText(this.formatMeasurement(nearestGap), lblX - perpX * labelOffset, lblY - perpY * labelOffset);
  }

  _drawDoorSwingEdit(scale) {
    const door = this._doorSwingEdit.door;
    const wall = door.wall;
    const doorWidthFeet = door.width / 12;
    const hingePoint = this.paramToPoint(wall, door.hingeParam);
    const endPoint = this.paramToPoint(wall, door.endParam);
    const wdx = wall.end.x - wall.start.x, wdy = wall.end.y - wall.start.y;
    const wLen = Math.sqrt(wdx * wdx + wdy * wdy);
    if (wLen === 0) return;
    const wallDirX = wdx / wLen, wallDirY = wdy / wLen;
    const perpX = -wallDirY, perpY = wallDirX;
    const seRoom = this.rooms.find(r => r.closed && r.doors && r.doors.includes(door)) || null;
    const offSE = this._getDoorOffset(wall, perpX, perpY, seRoom);
    const offHingeSE = { x: hingePoint.x + offSE.x, y: hingePoint.y + offSE.y };
    const offEndSE = { x: endPoint.x + offSE.x, y: endPoint.y + offSE.y };
    const midX = (offHingeSE.x + offEndSE.x) / 2, midY = (offHingeSE.y + offEndSE.y) / 2;

    const options = this._getSwingOptions(door.type);
    const mx = this.mouseWorldRaw.x, my = this.mouseWorldRaw.y;

    // Compute distance to door line for "closed" option
    const doorDx = offEndSE.x - offHingeSE.x, doorDy = offEndSE.y - offHingeSE.y;
    const doorLen = Math.sqrt(doorDx * doorDx + doorDy * doorDy);
    let closedDist = Infinity;
    if (doorLen > 0) {
      const t = ((mx - offHingeSE.x) * doorDx + (my - offHingeSE.y) * doorDy) / (doorLen * doorLen);
      const clampT = Math.max(0, Math.min(1, t));
      const closestX = offHingeSE.x + clampT * doorDx, closestY = offHingeSE.y + clampT * doorDy;
      closedDist = Math.sqrt((mx - closestX) ** 2 + (my - closestY) ** 2);
    }

    // Find nearest swing option
    let bestIdx = -1, bestDist = Infinity;
    for (let i = 0; i < options.length; i++) {
      const opt = options[i];
      let tipX, tipY;
      if (door.type === 'double') {
        tipX = midX + perpX * opt.perpSign * (doorWidthFeet / 2);
        tipY = midY + perpY * opt.perpSign * (doorWidthFeet / 2);
      } else {
        const pivotPt = opt.hingeEnd === 'hinge' ? offHingeSE : offEndSE;
        tipX = pivotPt.x + perpX * opt.perpSign * doorWidthFeet;
        tipY = pivotPt.y + perpY * opt.perpSign * doorWidthFeet;
      }
      const dist = Math.sqrt((mx - tipX) ** 2 + (my - tipY) ** 2);
      if (dist < bestDist) { bestDist = dist; bestIdx = i; }
    }

    const snapDist = Math.max(0.5, 15 / this.pixelsPerFoot);
    const closedHighlighted = closedDist < snapDist && closedDist < bestDist;
    if (closedHighlighted) bestIdx = -1;

    // Draw swing arcs and dots
    for (let i = 0; i < options.length; i++) {
      const opt = options[i];
      const highlighted = (i === bestIdx);
      const alpha = highlighted ? 0.45 : 0.15;
      if (door.type === 'double') {
        this._drawDoorSwingArc(offHingeSE, offEndSE, doorWidthFeet, wallDirX, wallDirY, perpX, perpY, opt.perpSign, null, 'double', scale, '#006BCC', alpha);
      } else {
        this._drawDoorSwingArc(offHingeSE, offEndSE, doorWidthFeet, wallDirX, wallDirY, perpX, perpY, opt.perpSign, opt.hingeEnd, 'single', scale, '#006BCC', alpha);
      }
      // Dot at swing tip
      let tipX, tipY;
      if (door.type === 'double') {
        tipX = midX + perpX * opt.perpSign * (doorWidthFeet / 2);
        tipY = midY + perpY * opt.perpSign * (doorWidthFeet / 2);
      } else {
        const pivotPt = opt.hingeEnd === 'hinge' ? offHingeSE : offEndSE;
        tipX = pivotPt.x + perpX * opt.perpSign * doorWidthFeet;
        tipY = pivotPt.y + perpY * opt.perpSign * doorWidthFeet;
      }
      this.ctx.fillStyle = highlighted ? 'rgba(0, 200, 0, 0.7)' : 'rgba(0, 107, 204, 0.3)';
      this.ctx.beginPath();
      this.ctx.arc(tipX, tipY, (highlighted ? 8 : 5) / scale, 0, Math.PI * 2);
      this.ctx.fill();
    }

    // Closed option dot at midpoint
    const closedDotX = midX, closedDotY = midY;
    this.ctx.fillStyle = closedHighlighted ? 'rgba(0, 200, 0, 0.7)' : 'rgba(0, 107, 204, 0.3)';
    this.ctx.beginPath();
    this.ctx.arc(closedDotX, closedDotY, (closedHighlighted ? 8 : 5) / scale, 0, Math.PI * 2);
    this.ctx.fill();

    if (closedHighlighted) {
      this.ctx.save();
      this.ctx.strokeStyle = '#006BCC';
      this.ctx.lineWidth = 3 / scale;
      this.ctx.globalAlpha = 0.6;
      this.ctx.beginPath();
      this.ctx.moveTo(offHingeSE.x, offHingeSE.y);
      this.ctx.lineTo(offEndSE.x, offEndSE.y);
      this.ctx.stroke();
      this.ctx.restore();
    }
  }

  _drawDoorDragMeasurements(scale) {
    this.selectedDoors.forEach(door => {
      const wall = door.wall;
      const wallLen = this.getWallLength(wall);
      if (wallLen < 0.01) return;
      const wdx = wall.end.x - wall.start.x, wdy = wall.end.y - wall.start.y;
      const wLen = Math.sqrt(wdx * wdx + wdy * wdy);
      const perpX = wLen > 0 ? -wdy / wLen : 0, perpY = wLen > 0 ? wdx / wLen : 0;
      const labelOffset = 18 / scale;
      const fontSize = 11 / scale;
      this.ctx.font = `${fontSize}px Arial`;
      this.ctx.fillStyle = '#006BCC';
      this.ctx.textAlign = 'center';
      this.ctx.textBaseline = 'middle';
      // Distance from wall start to door hinge
      const hingeP = Math.min(door.hingeParam, door.endParam);
      const endP = Math.max(door.hingeParam, door.endParam);
      const distToStart = hingeP * wallLen;
      const distToEnd = (1 - endP) * wallLen;
      if (distToStart > 0.01) {
        const midToStart = this.paramToPoint(wall, hingeP / 2);
        this.ctx.fillText(this.formatMeasurement(distToStart), midToStart.x + perpX * labelOffset, midToStart.y + perpY * labelOffset);
      }
      if (distToEnd > 0.01) {
        const midToEnd = this.paramToPoint(wall, endP + (1 - endP) / 2);
        this.ctx.fillText(this.formatMeasurement(distToEnd), midToEnd.x + perpX * labelOffset, midToEnd.y + perpY * labelOffset);
      }
      this.ctx.textAlign = 'start';
      this.ctx.textBaseline = 'alphabetic';
    });
  }

  /* ──────────────────────────────────────────
     DOOR DRAWING
     ────────────────────────────────────────── */

  _getWallOutwardSign(wall, perpX, perpY, room) {
    if (!room) {
      for (const r of this.rooms) {
        if (!r.closed) continue;
        for (const w of r.walls) { if (w === wall) { room = r; break; } }
        if (room) break;
      }
    }
    if (!room) return 1;
    // CUSTOM_ROOMDESIGNER_BY_CLAUDE — a wall-segment GROUP duck-types as a room for
    // doors (it carries doors[], which is why findNearestWall hands it back), but it
    // holds `segments`, not `walls`, and has no interior for anything to be outward
    // OF. _roomGeoFingerprint iterates room.walls, so a group reaching here THREW —
    // inside _doRender, taking the whole frame with it. That is why hovering a
    // curved DIVIDING wall showed no door preview at all while a curved perimeter
    // wall previewed fine: the curve path is the only one that asks for the outward
    // sign directly. _getDoorOffset has always had this guard; this never did.
    if (!room.walls) return 1;
    const geo = this._getRoomGeoCache(room);
    const { roomPolygon, cutoutPolys } = geo;
    if (!roomPolygon || roomPolygon.length < 3) return 1;
    const midX = (wall.start.x + wall.end.x) / 2, midY = (wall.start.y + wall.end.y) / 2;
    const testDist = 0.1;
    const t1x = midX + perpX * testDist, t1y = midY + perpY * testDist;
    const t2x = midX - perpX * testDist, t2y = midY - perpY * testDist;
    const isFilled = (tx, ty) => {
      if (!this.pointInPolygon(tx, ty, roomPolygon)) return false;
      if (cutoutPolys) { for (const cp of cutoutPolys) { if (cp && cp.length >= 3 && this.pointInPolygon(tx, ty, cp)) return false; } }
      return true;
    };
    const s1 = isFilled(t1x, t1y), s2 = isFilled(t2x, t2y);
    if (s1 && !s2) return -1;
    if (!s1 && s2) return 1;
    return 1;
  }

  _getDoorOffset(wall, perpX, perpY, room) {
    if (!room || !room.closed) return { x: 0, y: 0 };
    const outwardSign = this._getWallOutwardSign(wall, perpX, perpY, room);
    const halfT = this.wallThickness / 2;
    return { x: outwardSign * perpX * halfT, y: outwardSign * perpY * halfT };
  }

  /* Erase the wall band across a door opening. NOTE this is the PLACEMENT-PREVIEW
     path only — a placed door restores the pre-wall snapshot in drawDoor instead.
     ⚠️ Worth stating because a fix for the turf/doorway sliver was once written here
     and did nothing: nothing below drawDoor calls this. */
  _clearDoorGap(p1, p2, gapWidth, scale, skipGrid) {
    const halfW = gapWidth / 2;
    const dx = p2.x - p1.x, dy = p2.y - p1.y;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    const nx = -dy / len * halfW, ny = dx / len * halfW;
    this.ctx.save();
    this.ctx.beginPath();
    this.ctx.moveTo(p1.x + nx, p1.y + ny);
    this.ctx.lineTo(p2.x + nx, p2.y + ny);
    this.ctx.lineTo(p2.x - nx, p2.y - ny);
    this.ctx.lineTo(p1.x - nx, p1.y - ny);
    this.ctx.closePath();
    this.ctx.clip();
    this.ctx.fillStyle = this.darkMode ? '#141c2e' : '#ffffff';
    this.ctx.fill();
    if (this.showGrid && !skipGrid) this.drawGrid();
    // Re-apply room fills so doors inside rooms keep the tint
    for (const room of this.rooms) {
      if (!room.closed) continue;
      const geo = this._getRoomGeoCache(room);
      const poly = geo.roomPolygon;
      if (!poly || poly.length < 3) continue;
      this.ctx.fillStyle = room.type === 'subtract' ? 'rgba(204, 0, 0, 0.08)' : 'rgba(0, 107, 204, 0.08)';
      this.ctx.beginPath();
      this.ctx.moveTo(poly[0].x, poly[0].y);
      for (let i = 1; i < poly.length; i++) this.ctx.lineTo(poly[i].x, poly[i].y);
      this.ctx.closePath();
      this.ctx.fill();
    }
    this.ctx.restore();
  }

  drawDoor(door, room) {
    const scale = this.basePixelsPerFoot * this.zoomLevel;
    const wall = door.wall;
    const hingePoint = this.paramToPoint(wall, door.hingeParam);
    const endPoint = this.paramToPoint(wall, door.endParam);

    const wdx = wall.end.x - wall.start.x;
    const wdy = wall.end.y - wall.start.y;
    const wLen = Math.sqrt(wdx * wdx + wdy * wdy);
    if (wLen === 0) return;
    const wallDirX = wdx / wLen;
    const wallDirY = wdy / wLen;
    const perpX = -wallDirY;
    const perpY = wallDirX;

    // Offset door graphic to center in wall thickness (away from blue fill)
    const off = this._getDoorOffset(wall, perpX, perpY, room);
    const offHinge = { x: hingePoint.x + off.x, y: hingePoint.y + off.y };
    const offEnd = { x: endPoint.x + off.x, y: endPoint.y + off.y };

    // Restore pre-wall canvas content in the door gap so the underlying grid,
    // room fills, flooring overlay, overage, etc. show through naturally.
    const isSegmentDoor = room && Array.isArray(room.segments);
    const gapThickness = isSegmentDoor ? this.wallSegmentThickness : this.wallThickness;
    const doorGapWidth = gapThickness + 3 / scale;
    if (this._doorSnapCanvas) {
      const halfW = doorGapWidth / 2 + 1 / scale;
      const gdx = offEnd.x - offHinge.x, gdy = offEnd.y - offHinge.y;
      const gLen = Math.sqrt(gdx * gdx + gdy * gdy) || 1;
      const pnx = -gdy / gLen, pny = gdx / gLen;
      /* CUSTOM_ROOMDESIGNER_BY_CLAUDE — the restore band has to reach the ROOM side
         of the wall, and it did not.
         ⚠️ It is centred on offHinge..offEnd, and that line is not the wall — it is
         the wall pushed OUTWARD by half the thickness (see _getDoorOffset, which
         centres the graphic in the band away from the blue fill). A symmetric band
         around it therefore covers the outer half of the wall and stops half a
         thickness short of the inner face, leaving an unrestored strip on the room
         side of every opening.

         That strip is the white sliver between turf and a roller door: the turf
         walls-only pass snapshots the canvas WITH the turf on it, so everything the
         band does restore comes back correctly — the room-side strip was simply
         never inside the rectangle.

         ⚠️ And the strip is `halfT * scale - 1px` wide, so it GROWS with scale — which
         is why it is barely visible on screen and obvious on a PDF, exactly as
         reported. A fixed pixel fudge would have hidden it at one zoom and not others.

         The extension is the magnitude of the offset actually applied, so it is
         exactly half a thickness when the graphic was pushed off the wall line and
         ZERO when it was not — a divider door gets `{0,0}` back from _getDoorOffset
         and must stay symmetric, or its band over-reaches into the room. */
      const offAlongN = off.x * pnx + off.y * pny;
      const outSgn = offAlongN >= 0 ? 1 : -1;
      const outEdge = outSgn * halfW;                       // unchanged, outward
      const inEdge = -outSgn * (halfW + Math.abs(offAlongN)); // reaches the inner face
      const aX = pnx * outEdge, aY = pny * outEdge;
      const bX = pnx * inEdge, bY = pny * inEdge;
      this.ctx.save();
      this.ctx.beginPath();
      this.ctx.moveTo(offHinge.x + aX, offHinge.y + aY);
      this.ctx.lineTo(offEnd.x + aX, offEnd.y + aY);
      this.ctx.lineTo(offEnd.x + bX, offEnd.y + bY);
      this.ctx.lineTo(offHinge.x + bX, offHinge.y + bY);
      this.ctx.closePath();
      this.ctx.clip();
      this.ctx.setTransform(1, 0, 0, 1, 0, 0);
      this.ctx.drawImage(this._doorSnapCanvas, 0, 0);
      this.ctx.restore();
    }

    const doorAlpha = 1.0;
    // CUSTOM_ROOMDESIGNER_BY_CLAUDE — every opening in a curve draws STRAIGHT across
    // its chord, rollers included: a door or window unit is a flat assembly set into
    // the wall, which is how it is built in practice. The wall itself still reads as
    // a curve — the chord sits inside the opening, which the gap restore above
    // erases, and both jambs land exactly on the arc. An arc-swept roller bar was
    // tried and rejected; don't reintroduce it.
    // This call draws the jamb ticks AND the door leaf. Deleting it leaves only the
    // swing arc, which is exactly what happened once already.
    this.drawDoorGraphic(offHinge, offEnd, wallDirX, wallDirY, perpX, perpY, door.type, scale, this._tc.wallColor, doorAlpha);

    // Render swing arc if present (hide in floor mode when seams are on — overlaps seam lines)
    if (door.swing && !(this._floorMode && this.showSeamLines)) {
      const doorWidth = this.getDistance(offHinge, offEnd);
      if (doorWidth > 0) {
        const s = door.swing;
        this._drawDoorSwingArc(offHinge, offEnd, doorWidth, wallDirX, wallDirY, perpX, perpY, s.perpSign, s.hingeEnd || null, door.type, scale, this._tc.wallColor, doorAlpha);
      }
    }
  }

  drawDoorGraphic(hingePoint, endPoint, wallDirX, wallDirY, perpX, perpY, doorType, scale, color, alpha) {
    this.ctx.save();
    this.ctx.globalAlpha = alpha;
    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = 2 / scale;

    const tickLen = 2 / 12;
    const inset = 1 / 12;
    const doorThickness = 1 / 12;

    if (doorType === 'single') {
      // Tick marks at hinge and end
      this.ctx.beginPath();
      this.ctx.moveTo(hingePoint.x + perpX * tickLen, hingePoint.y + perpY * tickLen);
      this.ctx.lineTo(hingePoint.x - perpX * tickLen, hingePoint.y - perpY * tickLen);
      this.ctx.stroke();

      this.ctx.beginPath();
      this.ctx.moveTo(endPoint.x + perpX * tickLen, endPoint.y + perpY * tickLen);
      this.ctx.lineTo(endPoint.x - perpX * tickLen, endPoint.y - perpY * tickLen);
      this.ctx.stroke();

      // Door leaf rectangle
      const dx = endPoint.x - hingePoint.x;
      const dy = endPoint.y - hingePoint.y;
      const len = Math.sqrt(dx * dx + dy * dy);
      const dirX = len > 0 ? dx / len : wallDirX;
      const dirY = len > 0 ? dy / len : wallDirY;

      const p1x = hingePoint.x + dirX * inset + perpX * doorThickness;
      const p1y = hingePoint.y + dirY * inset + perpY * doorThickness;
      const p2x = endPoint.x - dirX * inset + perpX * doorThickness;
      const p2y = endPoint.y - dirY * inset + perpY * doorThickness;
      const p3x = endPoint.x - dirX * inset - perpX * doorThickness;
      const p3y = endPoint.y - dirY * inset - perpY * doorThickness;
      const p4x = hingePoint.x + dirX * inset - perpX * doorThickness;
      const p4y = hingePoint.y + dirY * inset - perpY * doorThickness;

      this.ctx.beginPath();
      this.ctx.moveTo(p1x, p1y);
      this.ctx.lineTo(p2x, p2y);
      this.ctx.lineTo(p3x, p3y);
      this.ctx.lineTo(p4x, p4y);
      this.ctx.closePath();
      if (!this.hollowWalls) {
        this.ctx.fillStyle = color;
        this.ctx.fill();
      }
      this.ctx.stroke();

    } else if (doorType === 'double') {
      const midX = (hingePoint.x + endPoint.x) / 2;
      const midY = (hingePoint.y + endPoint.y) / 2;

      // Three tick marks
      this.ctx.beginPath();
      this.ctx.moveTo(hingePoint.x + perpX * tickLen, hingePoint.y + perpY * tickLen);
      this.ctx.lineTo(hingePoint.x - perpX * tickLen, hingePoint.y - perpY * tickLen);
      this.ctx.stroke();

      this.ctx.beginPath();
      this.ctx.moveTo(midX + perpX * tickLen, midY + perpY * tickLen);
      this.ctx.lineTo(midX - perpX * tickLen, midY - perpY * tickLen);
      this.ctx.stroke();

      this.ctx.beginPath();
      this.ctx.moveTo(endPoint.x + perpX * tickLen, endPoint.y + perpY * tickLen);
      this.ctx.lineTo(endPoint.x - perpX * tickLen, endPoint.y - perpY * tickLen);
      this.ctx.stroke();

      // Two door leaves
      const dx = endPoint.x - hingePoint.x;
      const dy = endPoint.y - hingePoint.y;
      const len = Math.sqrt(dx * dx + dy * dy);
      const dirX = len > 0 ? dx / len : wallDirX;
      const dirY = len > 0 ? dy / len : wallDirY;

      const gapFromMid = 1.5 / 12;

      // Left leaf (hinge to mid)
      const l1x = hingePoint.x + dirX * inset + perpX * doorThickness;
      const l1y = hingePoint.y + dirY * inset + perpY * doorThickness;
      const l2x = midX - dirX * gapFromMid + perpX * doorThickness;
      const l2y = midY - dirY * gapFromMid + perpY * doorThickness;
      const l3x = midX - dirX * gapFromMid - perpX * doorThickness;
      const l3y = midY - dirY * gapFromMid - perpY * doorThickness;
      const l4x = hingePoint.x + dirX * inset - perpX * doorThickness;
      const l4y = hingePoint.y + dirY * inset - perpY * doorThickness;

      this.ctx.beginPath();
      this.ctx.moveTo(l1x, l1y);
      this.ctx.lineTo(l2x, l2y);
      this.ctx.lineTo(l3x, l3y);
      this.ctx.lineTo(l4x, l4y);
      this.ctx.closePath();
      if (!this.hollowWalls) {
        this.ctx.fillStyle = color;
        this.ctx.fill();
      }
      this.ctx.stroke();

      // Right leaf (mid to end)
      const r1x = midX + dirX * gapFromMid + perpX * doorThickness;
      const r1y = midY + dirY * gapFromMid + perpY * doorThickness;
      const r2x = endPoint.x - dirX * inset + perpX * doorThickness;
      const r2y = endPoint.y - dirY * inset + perpY * doorThickness;
      const r3x = endPoint.x - dirX * inset - perpX * doorThickness;
      const r3y = endPoint.y - dirY * inset - perpY * doorThickness;
      const r4x = midX + dirX * gapFromMid - perpX * doorThickness;
      const r4y = midY + dirY * gapFromMid - perpY * doorThickness;

      this.ctx.beginPath();
      this.ctx.moveTo(r1x, r1y);
      this.ctx.lineTo(r2x, r2y);
      this.ctx.lineTo(r3x, r3y);
      this.ctx.lineTo(r4x, r4y);
      this.ctx.closePath();
      if (!this.hollowWalls) {
        this.ctx.fillStyle = color;
        this.ctx.fill();
      }
      this.ctx.stroke();
    } else if (doorType === 'roller') {
      // Same design as single door (tick marks + leaf rectangle), no swing arc
      this.ctx.beginPath();
      this.ctx.moveTo(hingePoint.x + perpX * tickLen, hingePoint.y + perpY * tickLen);
      this.ctx.lineTo(hingePoint.x - perpX * tickLen, hingePoint.y - perpY * tickLen);
      this.ctx.stroke();

      this.ctx.beginPath();
      this.ctx.moveTo(endPoint.x + perpX * tickLen, endPoint.y + perpY * tickLen);
      this.ctx.lineTo(endPoint.x - perpX * tickLen, endPoint.y - perpY * tickLen);
      this.ctx.stroke();

      const dx = endPoint.x - hingePoint.x;
      const dy = endPoint.y - hingePoint.y;
      const len = Math.sqrt(dx * dx + dy * dy);
      const dirX = len > 0 ? dx / len : wallDirX;
      const dirY = len > 0 ? dy / len : wallDirY;

      const p1x = hingePoint.x + dirX * inset + perpX * doorThickness;
      const p1y = hingePoint.y + dirY * inset + perpY * doorThickness;
      const p2x = endPoint.x - dirX * inset + perpX * doorThickness;
      const p2y = endPoint.y - dirY * inset + perpY * doorThickness;
      const p3x = endPoint.x - dirX * inset - perpX * doorThickness;
      const p3y = endPoint.y - dirY * inset - perpY * doorThickness;
      const p4x = hingePoint.x + dirX * inset - perpX * doorThickness;
      const p4y = hingePoint.y + dirY * inset - perpY * doorThickness;

      this.ctx.beginPath();
      this.ctx.moveTo(p1x, p1y);
      this.ctx.lineTo(p2x, p2y);
      this.ctx.lineTo(p3x, p3y);
      this.ctx.lineTo(p4x, p4y);
      this.ctx.closePath();
      if (!this.hollowWalls) {
        this.ctx.fillStyle = color;
        this.ctx.fill();
      }
      this.ctx.stroke();
    }

    this.ctx.restore();
  }

}

window.RoomDesignerDoor = RoomDesignerDoor;

